import type { CAPRouter, Dataset } from "@phc/cap-service"
import type React from "react"
import { createContext, useContext, useMemo, useState } from "react"
import { useTRPC } from "../../utils/cap"

import { IS_PRD, IS_PROD } from "@phc/common"
import { useQuery, type UseQueryResult } from "@tanstack/react-query"
import type { inferRouterOutputs } from "@trpc/server"

export const isPrd = IS_PRD || IS_PROD

type RouterOutputs = inferRouterOutputs<CAPRouter>
type inferCapHook<T extends keyof RouterOutputs> = UseQueryResult<
  RouterOutputs[T],
  unknown
>

const CapServiceContext = createContext<{
  sanityDataset: Dataset
  setSanityDataset: React.Dispatch<React.SetStateAction<Dataset>>
  useGuidance: () => inferCapHook<"guidance">
  useAnalyses: () => inferCapHook<"analyses">
  useCategories: () => inferCapHook<"categories">
  useCriticalThreats: () => inferCapHook<"criticalThreats">
  useRelatedAnalyses: (id?: string) => inferCapHook<"relatedAnalyses">
  useSinglePost: (slug: string) => inferCapHook<"singlePost">
  useAlerts: (showAll?: boolean) => inferCapHook<"alerts">
  useRelatedAlerts: (id?: string) => inferCapHook<"relatedAlerts">
  useAdminPost: (tag: string) => inferCapHook<"adminPost">
  useFaqs: () => inferCapHook<"faq">
} | null>(null)

export const CapServiceContextProvider: React.FC<{
  children?: React.ReactNode
}> = ({ children }) => {
  const [sanityDataset, setSanityDataset] = useState<Dataset>(
    import.meta.env.VITE_LOCAL ? "development" : "production"
  )

  const context = useMemo(() => {
    const useGuidance = () => {
      const cap = useTRPC()
      return useQuery(cap.guidance.queryOptions({ dataset: sanityDataset }))
    }

    const useAnalyses = () => {
      const cap = useTRPC()
      return useQuery(cap.analyses.queryOptions({ dataset: sanityDataset }))
    }

    const useCategories = () => {
      const cap = useTRPC()
      return useQuery(cap.categories.queryOptions({ dataset: sanityDataset }))
    }

    const useCriticalThreats = () => {
      const cap = useTRPC()
      return useQuery(
        cap.criticalThreats.queryOptions({ dataset: sanityDataset })
      )
    }

    const useRelatedAnalyses = (id?: string) => {
      const cap = useTRPC()
      return useQuery(
        cap.relatedAnalyses.queryOptions(
          { arg: id ?? undefined, dataset: sanityDataset },
          {
            enabled: !!id,
            staleTime: 1000 * 60 * 5,
          }
        )
      )
    }

    const useSinglePost = (slug: string) => {
      const cap = useTRPC()
      return useQuery(
        cap.singlePost.queryOptions({ arg: slug, dataset: sanityDataset })
      )
    }

    const useAlerts = (showAll = false) => {
      const cap = useTRPC()
      return useQuery(
        cap.alerts.queryOptions(
          { dataset: sanityDataset, arg: showAll ? "all" : undefined },
          {
            staleTime: 1000 * 60 * 5,
          }
        )
      )
    }

    const useRelatedAlerts = (id?: string) => {
      const cap = useTRPC()
      return useQuery(
        cap.relatedAlerts.queryOptions(
          { arg: id ?? undefined, dataset: sanityDataset },
          {
            enabled: !!id,
            staleTime: 1000 * 60 * 5,
          }
        )
      )
    }

    const useAdminPost = (tag: string) => {
      const cap = useTRPC()
      return useQuery(
        cap.adminPost.queryOptions({ arg: tag, dataset: sanityDataset })
      )
    }

    const useFaqs = () => {
      const cap = useTRPC()
      return useQuery(cap.faq.queryOptions({ dataset: sanityDataset }))
    }

    return {
      sanityDataset,
      setSanityDataset,
      useGuidance,
      useAnalyses,
      useCategories,
      useCriticalThreats,
      useRelatedAnalyses,
      useSinglePost,
      useAlerts,
      useRelatedAlerts,
      useAdminPost,
      useFaqs,
    }
  }, [sanityDataset])
  return (
    <CapServiceContext.Provider value={context}>
      {children}
    </CapServiceContext.Provider>
  )
}

export function useCapServiceContext() {
  const context = useContext(CapServiceContext)
  if (context == null) {
    throw new Error(
      "useCapServiceContext must be used within a CapServiceContext"
    )
  }
  return useMemo(() => context, [context])
}
